import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaQrcode, FaUsers, FaCog, FaHome, FaMedal, FaBullhorn, FaChartBar, FaUserShield, FaEnvelope, FaTags } from 'react-icons/fa';
import { LuScanBarcode } from 'react-icons/lu';
import { useMerchantAuth } from '../../../contexts/AuthContext';

const DEFAULT_LOGO = 'https://i.postimg.cc/KvpcFk31/Astra-Web-Miami-2.png';

const ADMIN_NAV = [
    {
        name: 'Merchants',
        icon: <FaUsers />,
        path: '/dashboard/loyalpass-admin/merchants',
        permission: 'loyalpass_admin',
    },
    {
        name: 'Dashboard Previews',
        icon: <FaChartBar />,
        path: '/dashboard/loyalpass-admin/dashboard-previews',
        permission: 'loyalpass_admin',
    },
    {
        name: 'Invite Merchant',
        icon: <FaEnvelope />,
        path: '/dashboard/loyalpass-admin/merchant-invite',
        permission: 'loyalpass_admin',
    },
    {
        name: 'Merchant Tiers',
        icon: <FaTags />,
        path: '/dashboard/loyalpass-admin/merchant-tier',
        permission: 'loyalpass_admin',
    },
];

const NAV_ITEMS = [
    { name: 'Dashboard', icon: <FaHome />, path: '/dashboard', permission: 'view_dashboard' },
    { name: 'Customers', icon: <FaUsers />, path: '/dashboard/customers', permission: 'view_passes' },
    { name: 'Scan Pass', icon: <LuScanBarcode />, path: '/dashboard/scan', permission: 'scan_pass' },
    { name: 'Generate Pass', icon: <FaQrcode />, path: '/dashboard/generate', permission: 'generate_pass' },
    { name: 'Rewards', icon: <FaMedal />, path: '/dashboard/rewards', permission: 'view_rewards' },
    { name: 'Marketing', icon: <FaBullhorn />, path: '/dashboard/marketing', permission: 'view_marketing' },
    { name: 'Staff', icon: <FaUserShield />, path: '/dashboard/staff', permission: 'view_staff' },
    { name: 'Settings', icon: <FaCog />, path: '/dashboard/settings/general', permission: 'view_settings' },
];

export default function Sidebar({ isMobile = false, onNavigate = () => {} }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user: currentUser, hasPermission, loyalpassAdmin } = useMerchantAuth();
    const [logoSrc, setLogoSrc] = useState('/logo2.png');

    const sourceItems = loyalpassAdmin ? ADMIN_NAV : NAV_ITEMS;
    const navItems = sourceItems.filter((item) => hasPermission(item.permission));

    return (
        <aside className={`${isMobile ? '' : 'hidden md:block'} w-64 bg-card p-6 shadow-md flex flex-col`}>
            <img
                src={logoSrc}
                alt="LoyalPass Logo"
                className={`${!isMobile ? '' : 'hidden md:block h-screen'} h-10 w-auto`}
                onError={() => setLogoSrc(DEFAULT_LOGO)}
            />
            <ul className="space-y-4 mt-2 flex-1">
                {navItems.map((item) => {
                    const isActive = (() => {
                        const p = location.pathname;
                        if (item.path.endsWith('/merchants')) {
                            return p === '/dashboard/loyalpass-admin/merchants' || p === '/dashboard/loyalpass-admin';
                        }
                        if (item.path.endsWith('/dashboard-previews')) {
                            return p.startsWith('/dashboard/loyalpass-admin/dashboard-previews');
                        }
                        if (item.path.endsWith('/merchant-invite')) {
                            return p === '/dashboard/loyalpass-admin/merchant-invite';
                        }
                        if (item.path.endsWith('/merchant-tier')) {
                            return p === '/dashboard/loyalpass-admin/merchant-tier';
                        }
                        if (item.path === '/dashboard/staff') {
                            return p.startsWith('/dashboard/staff');
                        }
                        return p === item.path;
                    })();
                    return (
                        <li
                            key={item.name}
                            onClick={() => {
                                navigate(item.path);
                                onNavigate(); // Close sidebar if mobile
                            }}
                            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition ${
                                isActive
                                    ? 'bg-primary text-white'
                                    : 'text-gray-700 hover:bg-dark hover:text-primary'
                            }`}
                        >
                            <span className="text-lg text-white">{item.icon}</span>
                            <span className="font-medium text-white">{item.name}</span>
                        </li>
                    );
                })}
            </ul>
            {currentUser?.email && (
                <div className="mt-auto pt-4 border-t border-white/20">
                    <div className="text-xs font-medium text-white/80 uppercase tracking-wide">
                        {(currentUser.roleNames || ['Owner']).join(', ')}
                    </div>
                    <div className="text-sm text-white/90 truncate mt-0.5" title={currentUser.email}>
                        {currentUser.email}
                    </div>
                </div>
            )}
        </aside>
    );
}
